import { useState, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { createUseStyles } from "$utils/createUseStyles";
import { theme } from "$utils/theme/bataillog";
import { Text, Button } from "$components";
import useAuth from "$hooks/useAuth";
import { PASS_REGEX, PASS_REGEX_ERROR_MESSAGE } from "../Connexion/constants";

interface IProps {
  toggleDrawer: any;
}

function UpdatePasswordForm({ toggleDrawer }: IProps) {
  const classes = useStyles();
  const [oldPassword, setOldPassword] = useState("");
  const [oldPasswordError, setOldPasswordError] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState("");
  const { modifyPasswordAndLogin, user, verifyPassword } = useAuth();
  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm({
    mode: "onChange",
  });

  const onSubmit = async (data) => {
    if (user && user.userInfo) {
      const { oldPassword, newPassword } = data;
      try {
        await modifyPasswordAndLogin(
          user.userInfo.sub,
          user.email,
          newPassword,
          true,
          oldPassword
        );
        toggleDrawer();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleNewPasswordFocus = async () => {
    if (oldPassword) {
      const result = await verifyPassword(
        user.userInfo.sub,
        user.email,
        oldPassword
      );
      if (!result) {
        setOldPasswordError(
          "Ce mot de passe ne correspond pas au mot de passe actuel."
        );
      } else {
        setOldPasswordError("");
      }
    }
  };

  const handleConfirmPasswordFocus = useCallback(() => {
    if (oldPassword === getValues("newPassword")) {
      setNewPasswordError(
        "Votre nouveau mot de passe doit être différent de l’ancien."
      );
    } else {
      setNewPasswordError("");
    }
  }, [oldPassword, getValues]);

  return (
    <>
      <Text type="h2">Modifier mon mot de passe</Text>
      <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        {/* Old Password Field */}
        <div>
          <div className={classes.inputContainer}>
            <Controller
              name="oldPassword"
              control={control}
              rules={{
                required: "Champ obligatoire",
              }}
              render={({ field }) => (
                <div>
                  <div>
                    <TextField
                      {...field}
                      label="Ancien mot de passe"
                      type={showOldPassword ? "text" : "password"}
                      variant="outlined"
                      error={Boolean(errors.oldPassword)}
                      className={classes.TextField}
                      onChange={(e) => {
                        setOldPassword(e.target.value);
                        field.onChange(e);
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() =>
                                setShowOldPassword(!showOldPassword)
                              }
                              edge="end"
                            >
                              {showOldPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                  <Text className={classes.errorField}>{oldPasswordError}</Text>
                </div>
              )}
            />
          </div>
          {/* New Password Field */}
          <div className={classes.inputContainer}>
            <Controller
              name="newPassword"
              control={control}
              rules={{
                required: "Champ obligatoire",
                pattern: {
                  value: PASS_REGEX,
                  message: PASS_REGEX_ERROR_MESSAGE,
                },
              }}
              render={({ field }) => (
                <div>
                  <div>
                    <TextField
                      {...field}
                      label="Nouveau mot de passe"
                      type={showNewPassword ? "text" : "password"}
                      variant="outlined"
                      error={Boolean(errors.newPassword)}
                      className={classes.TextField}
                      onFocus={handleNewPasswordFocus}
                      onPaste={(e) => {
                        field.onChange(e);
                        setValue(
                          "newPassword",
                          (e.target as HTMLInputElement).value
                        );
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                              edge="end"
                            >
                              {showNewPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                  <Text className={classes.errorField}>
                    {(errors.newPassword && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: errors.newPassword
                            .message as unknown as string,
                        }}
                      />
                    )) ||
                      newPasswordError}
                  </Text>
                </div>
              )}
            />
          </div>
          {/* Confirm New Password Field */}
          <div className={classes.inputContainer}>
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: "Champ obligatoire",
                validate: (value) =>
                  value === getValues("newPassword") ||
                  "Le mot de passe n'est pas identique",
              }}
              render={({ field }) => (
                <div>
                  <div>
                    <TextField
                      {...field}
                      label="Confirmation du nouveau mot de passe"
                      type={showConfirmPassword ? "text" : "password"}
                      variant="outlined"
                      error={Boolean(errors.confirmPassword)}
                      className={classes.TextField}
                      onFocus={handleConfirmPasswordFocus}
                      onPaste={(e) => {
                        field.onChange(e);
                        setValue(
                          "confirmPassword",
                          (e.target as HTMLInputElement).value
                        );
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                  <Text className={classes.errorField}>
                    {errors.confirmPassword && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: errors.confirmPassword
                            .message as unknown as string,
                        }}
                      />
                    )}
                  </Text>
                </div>
              )}
            />
          </div>
        </div>
        {/* </div> */}
        <Button
          type="submit"
          disabled={Boolean(
            oldPasswordError ||
            errors.newPassword ||
            newPasswordError ||
            errors.confirmPassword ||
            !oldPassword ||
            !getValues("newPassword") ||
            !getValues("confirmPassword")
          )}
        >
          Changer le mot de passe
        </Button>
      </form>
    </>
  );
}

const useStyles = createUseStyles(() => ({
  container: {},
  content: {},
  form: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "var(--seventyFiveVh)",
  },
  inputContainer: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
  },
  passwordSection: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    border: "0px",
    marginTop: 20,
    "& td.MuiTableCell-body:first-child": {
      fontWeight: "bold",
    },
    "& .MuiTableContainer-root": {
      boxShadow: "unset",
    },
    "& .MuiIconButton-label": {
      transform: "scale(0.6) !important",
    },
  },
  inputFormat: {
    "&.MuiFormControl-root": {
      margin: [20, 0, 0, 0],
      fontFamily: theme.font.workSans,
      height: 56,
      width: 550,
    },
    "& .MuiInputBase-input": {
      fontFamily: ` ${theme.font.workSans} !important`,
    },
  },
  TextField: {
    composes: "$inputFormat",
  },
  errorField: {
    minHeight: 20,
    color: theme.color.red,
    fontSize: 12,
    maxWidth: 550,
  },
  disableTextField: {
    backgroundColor: "#F4F5F7",
  },
  formGroup: {
    display: "flex",
    justifyContent: "space-between",
  },
  btnGeneric: {
    height: "40px",
    "& .MuiButton-label > span": {
      color: theme.color.white,
    },
  },
}));

export default UpdatePasswordForm;
